import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Menu, X, Sparkles, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import bloomLogo from '@/assets/bloom-logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { scrollY } = useScroll();
  const { user, logout } = useAuth();
  
  const backgroundColor = useTransform(
    scrollY,
    [0, 40],
    ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0.78)']
  );
  
  const backdropBlur = useTransform(
    scrollY,
    [0, 40],
    ['blur(0px)', 'blur(10px)']
  );

  const boxShadow = useTransform(
    scrollY,
    [0, 40],
    ['0 0 0 rgba(0,0,0,0)', '0 4px 20px rgba(0,0,0,0.1)']
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: mounted ? backgroundColor : undefined,
        backdropFilter: backdropBlur,
        boxShadow
      }}
    >
      <div className="container mx-auto px-4 h-20 flex items-center justify-between relative z-10">
        <motion.div 
          className="flex items-center space-x-3 group"
          whileHover={{ scale: 1.05 }}
        >
          <div className="relative transition-all duration-300 group-hover:scale-110">
            <img 
              src={bloomLogo} 
              alt="Bloom Logo" 
              className="h-16 w-16 object-contain"
            />
          </div>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
          {['Features', 'How It Works', 'Demo', 'Security'].map((item, index) => (
            <motion.a
              key={item}
              href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
              className="relative text-gray-600 hover:text-cyan-600 transition-colors font-medium group"
              whileHover={{ y: -2 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {item}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-300 group-hover:w-full" />
            </motion.a>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <>
              <Link href="/dashboard">
                <Button variant="ghost" className="text-gray-600 hover:text-cyan-600 transition-all duration-300 hover:scale-105">
                  Dashboard
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                className="text-gray-600 hover:text-red-600 transition-all duration-300 hover:scale-105"
                onClick={logout}
              >
                <LogOut className="mr-2 w-4 h-4" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link href="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-cyan-600 transition-all duration-300 hover:scale-105">
                  Login
                </Button>
              </Link>
              
              <Link href="/register">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button className="relative bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6 transition-all duration-300 group overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <Sparkles className="mr-2 w-4 h-4 relative z-10" />
                    <span className="relative z-10">Sign Up</span>
                  </Button>
                </motion.div>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 hover:bg-cyan-50 rounded-lg transition-colors duration-300"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div
        className={cn(
          "md:hidden absolute top-16 right-0 bg-white rounded-l-lg shadow-xl border border-gray-200 min-w-[200px] z-50"
        )}
        initial={{ opacity: 0, y: -10 }}
        animate={{ 
          opacity: isMenuOpen ? 1 : 0, 
          y: isMenuOpen ? 0 : -10,
          pointerEvents: isMenuOpen ? 'auto' : 'none'
        }}
        transition={{ duration: 0.3 }}
      >
        <nav className="container mx-auto px-4 py-4 space-y-4">
          {['Features', 'How It Works', 'Demo', 'Security'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
              className="block text-gray-600 hover:text-cyan-600 transition-colors font-medium hover:translate-x-2 duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              {item}
            </a>
          ))}
          <div className="pt-2 border-t border-gray-200 space-y-2">
            {user ? (
              <>
                <Link href="/dashboard">
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                    <Sparkles className="mr-2 w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                  onClick={logout}
                >
                  <LogOut className="mr-2 w-4 h-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link href="/login">
                  <Button variant="ghost" className="w-full text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 font-medium h-10 transition-all duration-300">
                    Login
                  </Button>
                </Link>
                <Link href="/register">
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white flex justify-center items-center">
                    <Sparkles className="mr-2 w-4 h-4" />
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </nav>
      </motion.div>
    </motion.header>
  );
};

export default Header; 